import apiReq from 'core/api';
import type { Role, Permission } from 'types';

interface BackendPermission {
  id?: string | null;
  name?: string | null;
  title?: string | null;
  description?: string | null;
}

interface BackendRole {
  id: string | number;
  name: string;
  title?: string | null;
  description?: string | null;
  permissions?: BackendPermission[];
}

/** Тело форм `CreateRoleForm` / `UpdateRoleForm` (без `id`). */
export interface RoleFormPayload {
  name: string;
  title: string | null;
  description: string | null;
  /** Полный список пермишенов роли. Пустой массив очищает связи. */
  permissions: Array<{ name: string; title: string }>;
}

function mapPermission(p: BackendPermission): Permission {
  return {
    id: p.id ?? null,
    name: p.name ?? '',
    title: p.title ?? null,
    description: p.description ?? null,
  };
}

/** Маппинг сырой роли бэкенда в доменную модель `Role`. */
export function mapBackendRole(r: BackendRole): Role {
  return {
    id: String(r.id),
    name: r.name,
    title: r.title ?? null,
    description: r.description ?? null,
    permissions: (r.permissions ?? []).map(mapPermission),
  };
}

/** `GET /roles/all` — список всех ролей. */
export async function getRoles(): Promise<Role[]> {
  const res = await apiReq<BackendRole[]>('roles/all');
  return (res ?? []).map(mapBackendRole);
}

/** `GET /roles/{id}` — роль по ID. */
export async function getRole(id: string): Promise<Role | undefined> {
  const res = await apiReq<BackendRole>(`roles/${id}`);
  return res ? mapBackendRole(res) : undefined;
}

/** `GET /roles/permissions/all` — справочник пермишенов. */
export async function getPermissions(): Promise<Permission[]> {
  const res = await apiReq<BackendPermission[]>('roles/permissions/all');
  return (res ?? []).map(mapPermission);
}

/** `POST /roles/create` — создать роль. */
export function createRole(payload: RoleFormPayload): Promise<unknown> {
  return apiReq('roles/create', 'POST', payload);
}

/** `POST /roles/update` — обновить роль. */
export function updateRole(id: string, payload: RoleFormPayload): Promise<unknown> {
  return apiReq('roles/update', 'POST', { id, ...payload });
}

/** `DELETE /roles/delete?role_id=` — удалить роль. */
export function deleteRole(id: string): Promise<unknown> {
  return apiReq(`roles/delete?role_id=${encodeURIComponent(id)}`, 'DELETE');
}
